import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
    FaTimes, 
    FaPlay, 
    FaPause, 
    FaWifi, 
    FaRegHeart, 
    FaLock,
    FaMusic,
    FaSearch,
    FaChevronRight
} from 'react-icons/fa';
import { MdSkipNext } from 'react-icons/md';
import { toast } from 'react-toastify';
import { getRadioStationList } from '../../../../components/Api/RadioStationApi';
import { playRadioStationAction } from '../../../../Redux/Actions/RadioStationAction';
import { getItem } from '../../../../Utils/localStorageUtils';
import { useMyContext } from '../../../../components/Layout/CommonProvider';
import './Styles/NextStationPanel.css';

const NextStationPanel = ({ currentStation, onClose, onStationSelect }) => {
    const { navigateTranslateChecker } = useMyContext();
    const dispatch = useDispatch();
    const { currentAudio, currentPlaying } = useSelector((state) => state.get_current_Reducers || {});
    
    const [stations, setStations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredStations, setFilteredStations] = useState([]);

    // Load available stations
    useEffect(() => {
        loadNextStations();
    }, []);

    // Filter stations based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredStations(stations);
        } else {
            const filtered = stations.filter(station => 
                station.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                station.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredStations(filtered);
        }
    }, [searchTerm, stations]);

    const loadNextStations = async () => {
        setLoading(true);
        setError(null);

        try {
            const userRole = parseInt(getItem('role'), 10) || 9;
            const userId = parseInt(getItem('user_id'), 10) || null;

            console.log('🔄 Loading next stations...');
            const response = await getRadioStationList({
                Login_device_type: 'web',
                Login_user_type: userRole,
                Login_user_id: userId,
                limit: 50
            });

            if (response.data?.status && response.data?.data) {
                const stationList = response.data.data;
                // Remove current station from the list
                const otherStations = stationList.filter(station => 
                    station.id !== currentStation?.id
                );
                
                console.log('✅ Loaded next stations:', otherStations.length);
                setStations(otherStations);
                setFilteredStations(otherStations);
            } else {
                setError('No stations available');
            }
        } catch (err) {
            console.error('❌ Error loading next stations:', err);
            setError('Failed to load stations');
            toast.error('Failed to load stations');
        } finally {
            setLoading(false);
        }
    };

    // Check if user has access to station
    const checkStationAccess = useCallback((station) => {
        if (!station) return false;
        
        const requiresPayment = station.access === 'ppv' || station.access === 'paid';
        const isLoggedIn = !!getItem('user_id');
        const userRole = parseInt(getItem('role'), 10);
        
        if (!requiresPayment || station.access === 'free' || station.access === 'guest') {
            return true;
        }
        
        if (!isLoggedIn && requiresPayment) {
            return false;
        }
        
        const hasValidSubscription = userRole === 1 || userRole === 2;
        return hasValidSubscription;
    }, []);

    // Handle station selection
    const handleStationSelect = async (station) => {
        if (!station) return;

        console.log('🎵 Selecting next station:', station.title);
        
        // Check access
        if (!checkStationAccess(station)) {
            toast.error('This station requires a premium subscription');
            return;
        }

        try {
            // Dispatch action to play the station
            dispatch(playRadioStationAction(station));
            
            // Call the callback if provided
            if (onStationSelect) {
                onStationSelect(station);
            }
            
            // Close the panel
            onClose();
            
            toast.success(`Switched to ${station.title}`);
        } catch (error) {
            console.error('❌ Error selecting station:', error);
            toast.error('Failed to switch station');
        }
    };

    // Get valid audio URL for preview
    const getValidAudioUrl = (station) => {
        if (!station) return null;
        
        // Check for direct audio URLs first
        const directUrls = [
            station.mp4_url,
            station.aac_url,
            station.m3u8_url,
            station.acc_audio_file
        ];
        
        for (const url of directUrls) {
            if (url && isDirectAudioUrl(url)) {
                return url;
            }
        }
        
        // Check radio_url for both direct audio and embed
        if (station.radio_url) {
            if (isDirectAudioUrl(station.radio_url)) {
                return station.radio_url;
            } else if (isEmbedUrl(station.radio_url)) {
                // Return embed info to indicate it's playable
                return { type: 'embed', url: station.radio_url };
            }
        }
        
        return null;
    };
    
    // Check if URL is a direct audio URL
    const isDirectAudioUrl = (url) => {
        if (!url) return false;
        const urlLower = url.toLowerCase();
        
        // Check for direct audio file extensions
        const audioExtensions = ['.mp3', '.aac', '.ogg', '.wav', '.m3u', '.m3u8', '.pls'];
        if (audioExtensions.some(ext => urlLower.includes(ext))) {
            return true;
        }
        
        // Check for streaming domains and patterns
        const streamingPatterns = [
            'stream.', 'radio.', 'audio.', 'listen.', 'icecast', 'shoutcast',
            '/stream', '/radio', '/live', ':8000', ':8080', ':9000'
        ];
        return streamingPatterns.some(pattern => urlLower.includes(pattern));
    };
    
    // Check if URL is an embed URL
    const isEmbedUrl = (url) => {
        if (!url) return false;
        const urlLower = url.toLowerCase();
        const embedPatterns = [
            'youtube.com', 'youtu.be', 'vimeo.com', 'soundcloud.com'
        ];
        return embedPatterns.some(pattern => urlLower.includes(pattern));
    };

    // Check if station is currently playing
    const isCurrentlyPlaying = (station) => {
        return currentAudio?.station_data?.id === station.id && currentPlaying;
    };

    return (
        <div className="next-station-panel-overlay">
            <div className="next-station-panel theme-bg-color">
                {/* Header */}
                <div className="next-panel-header">
                    <div className="panel-title-section">
                        <MdSkipNext className="panel-icon theme-text-color" />
                        <h2 className="panel-title theme-text-color">Next Stations</h2>
                    </div>
                    <button 
                        className="close-panel-btn theme-text-color"
                        onClick={onClose}
                        aria-label="Close next stations panel"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Search */}
                <div className="search-section">
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon theme-text-color" />
                        <input
                            type="text"
                            placeholder="Search stations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input theme-bg-color-secondary theme-text-color"
                        />
                    </div>
                </div>

                {/* Current Station Info */}
                {currentStation && (
                    <div className="current-station-info theme-bg-color-secondary">
                        <div className="current-badge">
                            <FaWifi className="live-icon" />
                            <span>CURRENTLY PLAYING</span>
                        </div>
                        <div className="current-station-details">
                            <img 
                                src={currentStation.image_URL || currentStation.player_image_URL || '/default-radio-station.png'}
                                alt={currentStation.title}
                                className="current-station-image"
                            />
                            <div className="current-station-text">
                                <h3 className="current-station-title theme-text-color">
                                    {currentStation.title}
                                </h3>
                                <p className="current-station-desc theme-text-color opacity-75">
                                    {currentStation.description}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Stations List */}
                <div className="stations-list-section">
                    {loading ? (
                        <div className="stations-loading">
                            <div className="loading-spinner-panel"></div>
                            <span className="theme-text-color">Loading stations...</span>
                        </div>
                    ) : error ? (
                        <div className="stations-error">
                            <FaMusic className="error-icon theme-text-color" />
                            <p className="theme-text-color">{error}</p>
                            <button 
                                className="retry-btn-panel theme-button-bg-color theme-button-text-color"
                                onClick={loadNextStations}
                            >
                                Retry
                            </button>
                        </div>
                    ) : filteredStations.length === 0 ? (
                        <div className="no-stations">
                            <FaSearch className="no-stations-icon theme-text-color" />
                            <p className="theme-text-color">
                                {searchTerm ? `No stations found for "${searchTerm}"` : 'No stations available'}
                            </p>
                        </div>
                    ) : (
                        <div className="stations-list">
                            {filteredStations.map((station, index) => {
                                const hasAccess = checkStationAccess(station);
                                const audioSource = getValidAudioUrl(station);
                                const hasValidUrl = !!audioSource;
                                const isEmbedSource = audioSource && typeof audioSource === 'object' && audioSource.type === 'embed';
                                const isPlaying = isCurrentlyPlaying(station);
                                
                                return (
                                    <div 
                                        key={station.id || index}
                                        className={`station-item ${!hasAccess ? 'locked' : ''} ${!hasValidUrl ? 'unavailable' : ''} theme-bg-color-secondary`}
                                        onClick={() => hasAccess && hasValidUrl && handleStationSelect(station)}
                                    >
                                        <div className="station-item-image">
                                            <img 
                                                src={station.image_URL || station.player_image_URL || '/default-radio-station.png'}
                                                alt={station.title}
                                                className="station-thumbnail"
                                                onError={(e) => {
                                                    e.target.src = '/default-radio-station.png';
                                                }}
                                            />
                                            
                                            {/* Play/Status Indicator */}
                                            <div className="station-status-overlay">
                                                {!hasAccess ? (
                                                    <FaLock className="status-icon locked-icon" />
                                                ) : !hasValidUrl ? (
                                                    <div className="unavailable-badge">
                                                        <span>N/A</span>
                                                    </div>
                                                ) : isPlaying ? (
                                                    <div className="playing-indicator">
                                                        <FaPause className="status-icon playing-icon" />
                                                        <div className="audio-bars">
                                                            <div className="bar bar1"></div>
                                                            <div className="bar bar2"></div>
                                                            <div className="bar bar3"></div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <FaPlay className="status-icon play-icon" />
                                                )}
                                            </div>

                                            {/* Live Badge */}
                                            {hasValidUrl && station.url_type !== 'embed_url' && (
                                                <div className="live-badge-small">
                                                    <FaWifi className="live-icon-small" />
                                                </div>
                                            )}
                                        </div>

                                        <div className="station-item-content">
                                            <div className="station-item-header">
                                                <h4 className="station-item-title theme-text-color">
                                                    {station.title}
                                                </h4>
                                                
                                                {hasAccess && hasValidUrl && (
                                                    <div className="station-actions">
                                                        <button 
                                                            className="action-btn like-btn theme-text-color"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                // TODO: Implement wishlist functionality
                                                            }}
                                                            title="Add to favorites"
                                                        >
                                                            <FaRegHeart />
                                                        </button>
                                                        <FaChevronRight className="next-arrow theme-text-color" />
                                                    </div>
                                                )}
                                            </div>
                                            
                                            <p className="station-item-desc theme-text-color opacity-75">
                                                {station.description || 'Live radio station'}
                                            </p>
                                            
                                            <div className="station-item-meta">
                                                <span className="station-type theme-text-color opacity-50">
                                                    {isEmbedSource ? 'Video/Embed Stream' : 'Live Radio'}
                                                </span>
                                                
                                                {!hasAccess && (
                                                    <span className="premium-badge">
                                                        <FaLock />
                                                        Premium
                                                    </span>
                                                )}
                                                
                                                {!hasValidUrl && hasAccess && (
                                                    <span className="unavailable-badge-text">
                                                        Unavailable
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="next-panel-footer">
                    <div className="footer-info theme-text-color opacity-75">
                        <span>Tap any station to play • {filteredStations.length} stations available</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NextStationPanel;